// AppContext.tsx
import { useLazyQuery, useMutation } from '@apollo/client';
import { PaymentGetCardDetailsQuery, UpdatePreferencesInput } from '__generated__/graphql';
import { logError, logger } from 'components/LogError';
import { isLoggedInVar } from 'graphql/cache';
import useApiMutation from 'hooks/useApiMutation';
import useStateReducer, { IUpdater } from 'hooks/useStateReducer';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { createContext, useContext, ReactNode, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BannerTypeEnum, ChargeMgmtAndSaveCardEnum, IManageFeeConfig, IMgmtHashRef } from 'types/commonTypes';
import { capitalizeFirstLetter } from 'utils';
import { GET_USER_PREFERENCES } from 'views/Accounts/graphql/getUserPreferences';
import { UPDATE_USER_PREFERENCES } from 'views/Accounts/graphql/updateUserPreferences';
import { contactUsApi } from 'views/Accounts/services/contactUsApi';
import { HowToPayEnum } from 'views/shared/HowToPay/type';
import { fetchMgmtFeeStatus, IManagementFeeSettingResponse } from 'views/shared/services/fetchManagementFeeStatus';
import { MgmtFeeTypeEnum, MgmtViewsEnum } from 'views/shared/types';
import useDecodeHash from 'hooks/useDecodeHash';
import { updateManagementFeeStatus } from 'views/shared/services/updateManagementFeeStatus';
import { useGetCheckoutDotComCards } from 'views/Accounts/hooks/useGetCheckoutDotComCards';
import { selectCheckoutDotComCards } from 'views/Accounts/utils';
import { ACCEPTED_PAYMENT_METHODS } from 'views/Accounts/constants';
import { GET_CHECKOUT_DOT_COM_CARDS } from 'views/Accounts/graphql/getCheckoutDotComCards';
import { AppContext } from './ContextProvider';

type MgmtFeeContextValue = {
  updatePreferenceMutation: (request: UpdatePreferencesInput) => Promise<void>;
  onFeeTypeSubmit: (mgmtFeeType: MgmtFeeTypeEnum) => void;
  updateState: (value: IUpdater<IManageFeeConfig<unknown>>) => void;
  resetState: () => void;
  onHowToPayFee: (value: HowToPayEnum) => void;
  triggerModelPopUp: () => void;
  state: IManageFeeConfig<unknown>;
};

const defaultState: IManageFeeConfig = {
  showBanner: false,
  showModal: false,
  bannerType: BannerTypeEnum.COMBINE_FEES,
  forceAction: false,
  showMore: false,
  viewType: MgmtViewsEnum.NONE,
  viewData: null,
  annualFeeAmount: 0,
  monthlyFeeAmount: 0,
  isLockedOut: false,
  mgmtHashRef: null,
  selectedFeeAmount: 0,
  selectedMgmtFeeType: MgmtFeeTypeEnum.MONTHLY,
  triggerModel: 0,
  accruedFee: 0,
  totalDueTodayAnnualFee: 0,
  totalDueTodayMonthlyFee: 0,
  onNavShowModel: false,
  hasMitCard: false,
  annualDiscount: 0,
  plan: 'annual',
};

const defaultFeeStatus: IManagementFeeSettingResponse = {
  userId: '',
  accountHolderId: -1,
  monthlyFee: 0,
  annuallyFee: 0,
  paidDateTime: '',
  paidAmount: 0,
  showModal: false,
  lockout: false,
  createdDateTime: '',
  updatedDateTime: '',
};
const ANNUAL_FEE_DISCOUNT = 0.1;

const MgmtFeeContext = createContext<MgmtFeeContextValue | undefined>(undefined);

export const MgmtProvider = ({ children }: { children: ReactNode }) => {
  const {
    state: {
      settings: { accountInfo },
    },
  } = useContext(AppContext);

  const { decodeHash } = useDecodeHash();
  const { enforceMgmtFeeRules } = useFlags();
  const isLogin = isLoggedInVar();
  const { state, updateState, resetState } = useStateReducer<IManageFeeConfig>(defaultState);
  const location = useLocation();

  const [getcards] = useLazyQuery<PaymentGetCardDetailsQuery>(GET_CHECKOUT_DOT_COM_CARDS);
  const [updatePreferenceMutationApi] = useMutation<{ request: UpdatePreferencesInput }>(UPDATE_USER_PREFERENCES);

  const triggerModelPopUp = () => {
    updateState((prev) => ({ triggerModel: prev.triggerModel + 1 }));
  };

  const accountViewNav = useMemo(() => {
    return location.state?.accountViewType;
  }, [location]);

  const updatePreferenceMutation = async (request: UpdatePreferencesInput) => {
    try {
      await updatePreferenceMutationApi({ variables: { request } });
    } catch (error) {
      logError(
        { message: 'Failed to update Management Fee pref', name: 'updateManagementFeePref' },
        { tags: { userFlow: 'pay_fee_preferences' } },
      );
    }
  };

  const userHasMitEnabledCard = async () => {
    try {
      const cardData = await getcards();

      const _cards = selectCheckoutDotComCards(cardData.data);

      const validCardInfo = _cards
        .map((card) => {
          const valid = ACCEPTED_PAYMENT_METHODS.find(
            (x) => x.toLocaleLowerCase() === (card?.scheme || '').toLocaleLowerCase(),
          );
          if (valid) {
            return card;
          }

          return null;
        })
        .filter((x) => x !== null && x?.mitConsentedFlag);

      return validCardInfo.length > 0;
    } catch (error) {
      return null;
    }
  };

  const onFeeTypeSubmit = async (mgmtFeeType: MgmtFeeTypeEnum) => {
    switch (mgmtFeeType) {
      case MgmtFeeTypeEnum.YEARLY:
        updateState({
          selectedFeeAmount: state.totalDueTodayAnnualFee,
          selectedMgmtFeeType: mgmtFeeType,
          viewType: MgmtViewsEnum.CHARGE_AND_SAVE_CARD,
          mgmtHashRef: {
            token: null,
            mgmtFeeType,
            howToPay: HowToPayEnum.CARD,
            amount: state.totalDueTodayAnnualFee,
          },
        });

        await updatePreferenceMutation({
          managementFeePayment: capitalizeFirstLetter(MgmtFeeTypeEnum.YEARLY),
          annualManagementFeePayType: capitalizeFirstLetter(HowToPayEnum.CARD),
          historicManagementFeePayType: capitalizeFirstLetter(HowToPayEnum.CARD),
        });

        break;
      case MgmtFeeTypeEnum.MONTHLY:
        if (state.totalDueTodayMonthlyFee > 0) {
          updateState({
            selectedFeeAmount: state.totalDueTodayMonthlyFee,
            selectedMgmtFeeType: mgmtFeeType,
            viewType: MgmtViewsEnum.CHARGE_AND_SAVE_CARD,
            mgmtHashRef: {
              token: null,
              mgmtFeeType,
              howToPay: HowToPayEnum.CARD,
              amount: state.totalDueTodayMonthlyFee,
            },
          });
        } else if (!state.hasMitCard) {
          updateState({
            selectedFeeAmount: 0,
            selectedMgmtFeeType: mgmtFeeType,
            viewType: MgmtViewsEnum.CHARGE_AND_SAVE_CARD,
            mgmtHashRef: {
              token: null,
              mgmtFeeType,
              howToPay: HowToPayEnum.CARD,
              amount: 0,
              action: 'addCard',
            },
          });
        } else {
          updateManagementFeeStatus({ lockout: false, paidAmount: 0, showModal: false });
          updateState({
            ...defaultState,
            viewType: MgmtViewsEnum.CONFIRMATION,
          });
        }

        await updatePreferenceMutation({
          managementFeePayment: capitalizeFirstLetter(MgmtFeeTypeEnum.MONTHLY),
          annualManagementFeePayType: capitalizeFirstLetter(HowToPayEnum.CARD),
          historicManagementFeePayType: capitalizeFirstLetter(HowToPayEnum.CARD),
        });

        break;
      default:
        break;
    }
  };

  const onHowToPayFee = async (value: HowToPayEnum) => {
    if (value === HowToPayEnum.CARD || value === HowToPayEnum.CHARGE_AND_SAVE_CARD) {
      updateState((prev) => ({
        viewType: MgmtViewsEnum.CHARGE_AND_SAVE_CARD,
        mgmtHashRef: {
          token: null,
          howToPay: HowToPayEnum.CARD,
          ...prev.mgmtHashRef,
        },
      }));
    }
  };

  const getPaymentStatus = async (): Promise<{ status: 'paySuccess' | 'payFailure' | 'none' }> => {
    const hashData = decodeHash<IMgmtHashRef>({
      successHash: ChargeMgmtAndSaveCardEnum.onChargeCardMgmtSuccess,
      failHash: ChargeMgmtAndSaveCardEnum.onChargeCardMgmtFailure,
    });

    if (!hashData) return { status: 'none' };

    if (hashData.isFailure) {
      logError(JSON.stringify({ message: 'Pay mgmt fee failure', error: { hashData: JSON.stringify(hashData) } }), {
        tags: { userFlow: 'pay_mgmt_fee' },
      });

      return { status: 'payFailure' };
    }

    if (hashData.isSuccess) {
      updateManagementFeeStatus({ lockout: false, paidAmount: hashData.data.amount, showModal: false });
      updateState({
        viewType: MgmtViewsEnum.CONFIRMATION,
      });
      return { status: 'paySuccess' };
    }

    return { status: 'none' };
  };

  const getMgmtFeeStatus = async (): Promise<IManagementFeeSettingResponse> => {
    const status = await fetchMgmtFeeStatus();

    if (status) {
      return status;
    }
    return defaultFeeStatus;
  };

  const initiateMgmtStatus = async () => {
    try {
      const { status: paymentStatus } = await getPaymentStatus();

      if (paymentStatus === 'paySuccess') {
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      const hasMitCard = await userHasMitEnabledCard();
      if (hasMitCard === null) throw new Error('Failed to get card on file');

      const status = await getMgmtFeeStatus();
      if (!status) throw new Error('Failed to get Management Fee Status');

      const { lockout, showModal } = status;

      const monthlyFee = status?.monthlyFee ?? 0;
      const annuallyFee = status?.annuallyFee ?? 0;

      let viewType = MgmtViewsEnum.NONE;

      if (showModal && paymentStatus === 'payFailure') {
        viewType = MgmtViewsEnum.CHARGE_AND_SAVE_CARD_FAILED;
      }

      if (showModal && paymentStatus === 'none') {
        viewType = MgmtViewsEnum.COMBINE_FEE_TYPE;
      }

      const accountBal = accountInfo?.account?.accountBalanceGBP ?? 0; // account balance in gbp
      const accruedFee = accountBal >= 0 ? 0 : Number(Math.abs(accountBal).toFixed(2));

      const annualDiscount = annuallyFee * ANNUAL_FEE_DISCOUNT;
      const totalDueTodayAnnualFee = accruedFee + (annuallyFee - annualDiscount);
      const totalDueTodayMonthlyFee = accruedFee;

      const values: Partial<IManageFeeConfig> = {
        showBanner: showModal,
        onNavShowModel: showModal,
        showModal,
        viewType,
        accruedFee,
        hasMitCard,
        isLockedOut: lockout,
        annualDiscount,
        annualFeeAmount: annuallyFee,
        monthlyFeeAmount: monthlyFee,
        selectedFeeAmount: totalDueTodayAnnualFee,
        totalDueTodayAnnualFee,
        totalDueTodayMonthlyFee,

        forceAction: false,
      };

      updateState(values);
    } catch (err) {
      logger({
        message: 'Failed to get Management Fee Status',
        name: 'ManagementFeeStatus',
        err: JSON.stringify(err),
        tags: { userFlow: 'pay_mgmt_fee' },
      });
    }
  };

  useEffect(() => {
    if (state.onNavShowModel && enforceMgmtFeeRules && accountInfo?.portfolios) {
      updateState({ showBanner: true, showModal: true, viewType: MgmtViewsEnum.COMBINE_FEE_TYPE });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, accountViewNav, enforceMgmtFeeRules, state.triggerModel, accountInfo?.portfolios]);

  useEffect(() => {
    if (enforceMgmtFeeRules && isLogin && accountInfo?.portfolios) {
      initiateMgmtStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enforceMgmtFeeRules, isLogin, accountInfo?.portfolios]);

  return (
    <MgmtFeeContext.Provider
      value={{
        onFeeTypeSubmit,
        onHowToPayFee,
        resetState,
        state,
        updatePreferenceMutation,
        updateState,
        triggerModelPopUp,
      }}
    >
      {children}
    </MgmtFeeContext.Provider>
  );
};

export const useMgmtFeeContext = () => {
  const ctx = useContext(MgmtFeeContext);
  if (!ctx) {
    throw new Error('MgmtFeeContext must be used inside AppProvider');
  }
  return ctx;
};
